/**
 * Pure helpers for editing listings in the admin (docs/phase-3-plan.md).
 * No Firebase, no i18n, no browser APIs; everything takes "now" as an argument.
 */
import type {
  CategoryKey,
  Contact,
  HoursEntry,
  Listing,
  ListingLocation,
  ListingMessage,
  ListingStatus,
} from '../types';
import { DEFAULT_AREA_ID } from '../config/areas';
import { getCategory, LISTING_SCHEMA_VERSION } from '../config/categories';
import { MAX_MESSAGE_MONTHS } from '../config/constants';
import { addDaysToDate, getIsraelNow, dateStringToNoonUtc } from './israelTime';
import { approximatePoint, hasCoords } from './geo';
import { approximateAddress } from './listingDisplay';

/** Size limits; the Firestore rules check the same numbers. */
export const LISTING_LIMITS = {
  nameLength: 200,
  textLength: 5000,
  noteLength: 500,
  addressLength: 300,
  messages: 30,
  contacts: 10,
  hours: 60,
  tags: 30,
} as const;

/** A new random id for contacts, hours entries and messages. */
export function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Removes undefined values everywhere (Firestore rejects them) and empty
 * strings from plain objects. Arrays keep their length.
 */
export function cleanForSave<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => cleanForSave(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined || v === '') continue;
      out[key] = cleanForSave(v);
    }
    return out as T;
  }
  return value;
}

// ---------- New and duplicated listings ----------

export interface NewListingInput {
  category: CategoryKey;
  /** Ignored for shuls. */
  subcategory?: string;
  nameHe: string;
  nameEn?: string;
  descriptionHe?: string;
  descriptionEn?: string;
  location?: Partial<ListingLocation>;
  contacts?: Contact[];
  /** When the person ticked "I may publish these details" (plan 16). */
  publishConsentAt?: string;
  /** Category details to start with (shiurim: where, shul, who gives it), over the defaults. */
  details?: Record<string, unknown>;
}

/** Category-specific starting details. Mikvaos need their type, which is the subcategory. */
export function defaultDetails(category: CategoryKey, subcategory?: string): Record<string, unknown> {
  if (category === 'mikvah') return { mikvahType: subcategory || 'mens' };
  if (category === 'shiur') return { where: 'place', audience: [], languages: [], sessions: [] };
  return {};
}

/** A complete new listing document (without id). */
export function newListing(
  input: NewListingInput,
  ownerUids: string[],
  status: ListingStatus,
  nowIso: string
): Omit<Listing, 'id'> {
  const config = getCategory(input.category);
  const base = {
    schemaVersion: LISTING_SCHEMA_VERSION,
    category: input.category,
    nameHe: input.nameHe.trim(),
    nameEn: input.nameEn?.trim(),
    descriptionHe: input.descriptionHe?.trim(),
    descriptionEn: input.descriptionEn?.trim(),
    messages: [],
    location: locationForSave({
      addressEn: '',
      addressHe: '',
      areaId: DEFAULT_AREA_ID,
      showExactLocation: true,
      ...input.location,
    }),
    contacts: cleanContacts(input.contacts ?? []),
    hours: [],
    hoursMode: config.defaultHoursMode,
    tags: [],
    ownerUids,
    status,
    publishConsentAt: input.publishConsentAt,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  const doc =
    input.category === 'shul'
      ? { ...base, details: {}, schedules: [], nusach: 'ashkenaz' }
      : {
          ...base,
          subcategory: input.subcategory || config.subcategories[0],
          details: { ...defaultDetails(input.category, input.subcategory), ...input.details },
        };
  return { ...cleanForSave(doc), location: base.location } as unknown as Omit<Listing, 'id'>;
}

/**
 * "Fill in from my listing" on Add a listing (plan 16): the address, map pin,
 * area and contacts of one of the user's listings, as a starting point for a
 * new listing of any category. Names, hours and details are not copied.
 * Contacts get new ids.
 */
export function fillFromListing(source: Pick<Listing, 'location' | 'contacts'>): {
  location: ListingLocation;
  contacts: Contact[];
} {
  return {
    location: { ...source.location },
    contacts: (source.contacts ?? []).map((c) => ({ ...c, id: newId() })),
  };
}

// ---------- Location ----------

/**
 * What to store for a location. When the exact location is hidden, the exact
 * point and the house number are never stored: only the approximate point and
 * the street (decided by Yoel, 2026-10-02).
 */
export function locationForSave(loc: ListingLocation): ListingLocation {
  const trimmed: ListingLocation = {
    ...loc,
    addressEn: (loc.addressEn ?? '').trim(),
    addressHe: (loc.addressHe ?? '').trim(),
    areaId: loc.areaId || DEFAULT_AREA_ID,
    showExactLocation: loc.showExactLocation !== false,
    directionsNoteEn: loc.directionsNoteEn?.trim(),
    directionsNoteHe: loc.directionsNoteHe?.trim(),
  };
  if (!hasCoords(trimmed)) {
    delete trimmed.lat;
    delete trimmed.lng;
  }
  if (trimmed.showExactLocation) return withAddresses(trimmed);

  const hidden: ListingLocation = {
    ...trimmed,
    addressEn: approximateAddress(trimmed.addressEn),
    addressHe: approximateAddress(trimmed.addressHe),
  };
  if (hasCoords(trimmed)) {
    const approx = approximatePoint(trimmed);
    hidden.lat = approx.lat;
    hidden.lng = approx.lng;
  }
  return withAddresses(hidden);
}

/** cleanForSave, but both address fields always exist (empty strings allowed). */
function withAddresses(loc: ListingLocation): ListingLocation {
  return { ...cleanForSave(loc), addressEn: loc.addressEn, addressHe: loc.addressHe };
}

// ---------- Contacts ----------

const CONTACT_FIELDS = ['nameEn', 'nameHe', 'phone', 'whatsapp', 'email', 'website'] as const;

export function isContactEmpty(contact: Contact): boolean {
  return CONTACT_FIELDS.every((f) => !contact[f]?.trim());
}

/** Trims every field and drops contacts with nothing filled in. */
export function cleanContacts(contacts: Contact[]): Contact[] {
  return contacts
    .map((c) => {
      const out: Contact = { id: c.id || newId() };
      for (const f of CONTACT_FIELDS) {
        const v = c[f]?.trim();
        if (v) out[f] = v;
      }
      return out;
    })
    .filter((c) => !isContactEmpty(c));
}

/** Moves one item up (-1) or down (+1); out-of-range moves leave the list unchanged. */
export function moveItem<T>(items: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

// ---------- Tags ----------

/** "stroller, עגלה ,stroller" → ['stroller', 'עגלה']. Splits on commas and new lines. */
export function parseTags(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,\n،]/)) {
    const tag = raw.trim();
    if (!tag || seen.has(tag.toLowerCase())) continue;
    seen.add(tag.toLowerCase());
    out.push(tag);
  }
  return out.slice(0, LISTING_LIMITS.tags);
}

// ---------- Messages ----------

export type MessageDuration = '1d' | '3d' | '1w' | '1m' | 'date';

export const MESSAGE_DURATIONS: MessageDuration[] = ['1d', '3d', '1w', '1m', 'date'];

/** The latest a message may end: MAX_MESSAGE_MONTHS calendar months after it starts. */
export function maxMessageExpiry(startIso: string): string {
  const d = new Date(startIso);
  d.setUTCMonth(d.getUTCMonth() + MAX_MESSAGE_MONTHS);
  return d.toISOString();
}

/** The moment a date ends in Israel (= midnight at the start of the next Israel day), as ISO. */
export function endOfIsraelDayIso(date: string): string {
  const next = addDaysToDate(date, 1);
  const noon = dateStringToNoonUtc(next);
  const israelNoon = getIsraelNow(noon).time; // "14:00" or "15:00"
  const offsetHours = Number(israelNoon.slice(0, 2)) - 12;
  return new Date(noon.getTime() - (12 + offsetHours) * 3600_000).toISOString();
}

/**
 * When a message posted at `startIso` should expire. `untilDate` ('YYYY-MM-DD',
 * Israel) is used for 'date': the message shows until the end of that day.
 * Never later than the 3-month maximum.
 */
export function messageExpiry(startIso: string, duration: MessageDuration, untilDate?: string): string {
  const start = Date.parse(startIso);
  const day = 24 * 3600_000;
  let end: string;
  switch (duration) {
    case '1d':
      end = new Date(start + day).toISOString();
      break;
    case '3d':
      end = new Date(start + 3 * day).toISOString();
      break;
    case '1w':
      end = new Date(start + 7 * day).toISOString();
      break;
    case '1m': {
      const d = new Date(start);
      d.setUTCMonth(d.getUTCMonth() + 1);
      end = d.toISOString();
      break;
    }
    case 'date':
      end = untilDate ? endOfIsraelDayIso(untilDate) : new Date(start + day).toISOString();
      break;
  }
  const max = maxMessageExpiry(startIso);
  return Date.parse(end) > Date.parse(max) ? max : end;
}

/** A new message starting now. */
export function newMessage(
  text: { textHe?: string; textEn?: string },
  nowIso: string,
  duration: MessageDuration,
  untilDate?: string
): ListingMessage {
  return cleanForSave({
    id: newId(),
    textHe: text.textHe?.trim(),
    textEn: text.textEn?.trim(),
    startsAt: nowIso,
    expiresAt: messageExpiry(nowIso, duration, untilDate),
  });
}

/** Messages that have already ended, newest first. */
export function expiredMessages(messages: ListingMessage[] | undefined, nowIso: string): ListingMessage[] {
  const now = Date.parse(nowIso);
  return (messages ?? [])
    .filter((m) => Date.parse(m.expiresAt) <= now)
    .sort((a, b) => Date.parse(b.expiresAt) - Date.parse(a.expiresAt));
}

/** Ends a message now (keeps it in the expired list). */
export function endMessageNow(messages: ListingMessage[], id: string, nowIso: string): ListingMessage[] {
  return messages.map((m) => (m.id === id ? { ...m, expiresAt: nowIso } : m));
}

/**
 * Keeps the newest messages within the limit, dropping the oldest expired
 * ones first, so the list never grows without end.
 */
export function trimMessages(messages: ListingMessage[], nowIso: string): ListingMessage[] {
  if (messages.length <= LISTING_LIMITS.messages) return messages;
  const now = Date.parse(nowIso);
  const live = messages.filter((m) => Date.parse(m.expiresAt) > now);
  const ended = expiredMessages(messages, nowIso);
  return [...live, ...ended].slice(0, LISTING_LIMITS.messages);
}

// ---------- Checks ----------

export type ListingFieldError =
  | 'nameHeRequired'
  | 'nameTooLong'
  | 'subcategoryInvalid'
  | 'textTooLong'
  | 'emailInvalid'
  | 'websiteInvalid'
  | 'tooManyContacts'
  | 'tooManyHours'
  | 'messageTextRequired'
  | 'hoursTimeMissing';

export interface ListingFieldsToCheck {
  category: CategoryKey;
  subcategory?: string;
  nameHe?: string;
  nameEn?: string;
  descriptionHe?: string;
  descriptionEn?: string;
  contacts?: Contact[];
  hours?: HoursEntry[];
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function isValidWebsite(url: string): boolean {
  return /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i.test(url.trim());
}

/** Adds https:// when the owner typed a bare "example.com". */
export function normalizeWebsite(url: string): string {
  const s = url.trim();
  return !s || /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

/** Problems that stop a save. Empty = OK. Each key has a translation in `directory.admin.error.*`. */
export function checkListingFields(fields: ListingFieldsToCheck): ListingFieldError[] {
  const errors = new Set<ListingFieldError>();
  if (!fields.nameHe?.trim()) errors.add('nameHeRequired');
  if ((fields.nameHe ?? '').length > LISTING_LIMITS.nameLength || (fields.nameEn ?? '').length > LISTING_LIMITS.nameLength) {
    errors.add('nameTooLong');
  }
  if (fields.category !== 'shul') {
    const subs = getCategory(fields.category).subcategories;
    if (!fields.subcategory || !subs.includes(fields.subcategory)) errors.add('subcategoryInvalid');
  }
  if (
    (fields.descriptionHe ?? '').length > LISTING_LIMITS.textLength ||
    (fields.descriptionEn ?? '').length > LISTING_LIMITS.textLength
  ) {
    errors.add('textTooLong');
  }
  for (const c of fields.contacts ?? []) {
    if (c.email?.trim() && !isValidEmail(c.email)) errors.add('emailInvalid');
    if (c.website?.trim() && !isValidWebsite(c.website)) errors.add('websiteInvalid');
  }
  if ((fields.contacts?.length ?? 0) > LISTING_LIMITS.contacts) errors.add('tooManyContacts');
  if ((fields.hours?.length ?? 0) > LISTING_LIMITS.hours) errors.add('tooManyHours');
  for (const h of fields.hours ?? []) {
    if (!h.closed && (!isTimeRuleComplete(h.open) || !isTimeRuleComplete(h.close))) {
      errors.add('hoursTimeMissing');
    }
  }
  return Array.from(errors);
}

function isTimeRuleComplete(rule: HoursEntry['open'] | undefined): boolean {
  if (!rule) return false;
  if (rule.type === 'fixed') return /^\d{2}:\d{2}$/.test(rule.time ?? '');
  return !!rule.baseZman;
}
